import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import ConversationList from "@/components/messages/ConversationList";
import MessagesList from "@/components/messages/MessagesList";
import ChatHeader from "@/components/messages/ChatHeader";
import MessageInput from "@/components/messages/MessageInput";
import CallDialogs from "@/components/messages/CallDialogs";
import { useMessages } from "@/hooks/useMessages";

interface Conversation {
  id: string;
  name: string;
  avatar: string;
  status: "online" | "offline";
  lastMessage: string;
  time: string;
  unread: number;
  userId: string;
}

const Messages = () => {
  const { user } = useAuth();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [showAttachMedia, setShowAttachMedia] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    searchTerm,
    setSearchTerm,
    isLoadingConversations,
    setIsLoadingConversations,
    isLoadingMessages,
    setIsLoadingMessages,
    isTyping,
    setIsTyping,
    typingUsers,
    setTypingUsers,
    selectedFile,
    setSelectedFile,
    onlineUsers,
    presenceChannelRef,
    channelRef,
    formatTimestamp,
    markMessagesAsRead,
    sendMessage,
    handleSelectConversation
  } = useMessages();

  useEffect(() => {
    if (!user?.id) {
      console.log("No user ID available, skipping conversation fetch");
      return;
    }

    const fetchConversations = async () => {
      setIsLoadingConversations(true);

      try {
        console.log("Fetching conversations for user:", user.id);

        // Fetch conversations where the current user is either the mentor or mentee
        const { data: conversationsData, error: conversationsError } = await supabase
          .from('conversations')
          .select(`
            id,
            mentor_id,
            mentee_id,
            last_message_text,
            last_message_time
          `)
          .or(`mentor_id.eq.${user.id},mentee_id.eq.${user.id}`);

        if (conversationsError) {
          console.error("Error fetching conversations:", conversationsError);
          toast.error("Failed to load conversations");
          setIsLoadingConversations(false);
          return;
        }

        console.log("Fetched conversations:", conversationsData);

        if (!conversationsData || conversationsData.length === 0) {
          console.log("No conversations found for user:", user.id);
          setConversations([]);
          setIsLoadingConversations(false);
          return;
        }

        // Now we'll fetch the profiles for each conversation
        const formattedConversations: Conversation[] = [];

        for (const conv of conversationsData) {
          // Determine if the user is the mentor or mentee
          const isUserMentor = conv.mentor_id === user.id;
          const otherUserId = isUserMentor ? conv.mentee_id : conv.mentor_id;

          console.log(`Processing conversation between ${conv.mentor_id} and ${conv.mentee_id}`);
          console.log(`Current user is ${isUserMentor ? 'mentor' : 'mentee'}, other user id: ${otherUserId}`);

          if (!otherUserId) {
            console.error("Missing other user ID in conversation:", conv);
            continue;
          }

          // Get the other user's profile details
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, avatar_url')
            .eq('id', otherUserId)
            .maybeSingle();

          if (profileError) {
            console.error("Error fetching profile:", profileError, "for user ID:", otherUserId);
            continue;
          }

          if (!profileData) {
            console.error("No profile found for user ID:", otherUserId);
            continue;
          }

          const fullName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'Anonymous User';

          formattedConversations.push({
            id: conv.id,
            name: fullName,
            avatar: profileData.avatar_url || '',
            status: onlineUsers.has(otherUserId) ? "online" : "offline",
            lastMessage: conv.last_message_text || 'No messages yet',
            time: conv.last_message_time ? new Date(conv.last_message_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'New',
            unread: 0, // Default to 0, will update later
            userId: otherUserId
          });
        }

        // Fetch unread message counts
        for (const conv of formattedConversations) {
          const { count, error: countError } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('is_read', false)
            .neq('sender_id', user.id);

          if (!countError && count !== null) {
            conv.unread = count;
          }
        }

        console.log("Formatted conversations:", formattedConversations);
        setConversations(formattedConversations);

        // Auto-select the conversation from the URL or the first conversation if not specified
        if (conversationId && formattedConversations.some(conv => conv.id === conversationId)) {
          setSelectedConversation(conversationId);
        } else if (formattedConversations.length > 0 && !selectedConversation) {
          setSelectedConversation(formattedConversations[0].id);
          navigate(`/messages/${formattedConversations[0].id}`, { replace: true });
        }
      } catch (error) {
        console.error("Exception in fetchConversations:", error);
        toast.error("An error occurred while loading your conversations");
      } finally {
        setIsLoadingConversations(false);
      }
    };

    fetchConversations();
  }, [user?.id, conversationId, navigate, selectedConversation, setConversations, setIsLoadingConversations, setSelectedConversation, onlineUsers]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, []);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setShowAttachMedia(false);
    toast.success(`File selected: ${file.name}`);
  };

  const handleAttachmentClick = () => {
    setShowAttachMedia(true);
  };

  const startVideoCall = () => {
    setShowVideoCall(true);
    toast.info("Starting video call...");
  };

  const startVoiceCall = () => {
    setShowVoiceCall(true);
    toast.info("Starting voice call...");
  };

  const endCall = () => {
    setShowVideoCall(false);
    setShowVoiceCall(false);
    toast.info("Call ended");
  };

  const getSelectedConversation = () => {
    return conversations.find(conv => conv.id === selectedConversation);
  };

  const handleSendMessage = (content: string, attachmentUrl?: string, attachmentName?: string) => {
    console.log("Handling send message with content:", content);
    sendMessage(content, attachmentUrl, attachmentName);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <div className="w-full bg-cover bg-center h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        <div className="container mx-auto px-4 h-full flex items-center relative z-10">
          <div className="pt-20 pb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Messages
            </h1>
            <p className="text-gray-600 mt-2">
              View and respond to your mentoring conversations.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-grow pt-8 pb-12 flex flex-col">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-lg shadow-sm border h-[calc(100vh-300px)] overflow-hidden flex flex-col">
            <div className="flex h-full">
              {showSidebar && (
                <ConversationList
                  conversations={conversations}
                  selectedConversation={selectedConversation}
                  searchTerm={searchTerm}
                  isLoading={isLoadingConversations}
                  onSearchChange={setSearchTerm}
                  onSelectConversation={(id) => {
                    handleSelectConversation(id);
                    if (window.innerWidth < 768) {
                      setShowSidebar(false);
                    }
                  }}
                />
              )}

              <div className="flex-1 flex flex-col">
                {selectedConversation ? (
                  <>
                    <ChatHeader
                      conversation={getSelectedConversation()}
                      showSidebar={showSidebar}
                      onToggleSidebar={() => setShowSidebar(!showSidebar)}
                      onStartVoiceCall={startVoiceCall}
                      onStartVideoCall={startVideoCall}
                    />

                    <div className="flex-1 p-4 overflow-y-auto bg-gray-50" ref={scrollAreaRef}>
                      <MessagesList
                        messages={messages}
                        currentUserId={user?.id || ""}
                        otherUserName={getSelectedConversation()?.name || ""}
                        otherUserAvatar={getSelectedConversation()?.avatar || ""}
                        isLoading={isLoadingMessages}
                        isTyping={isTyping}
                        typingUsers={typingUsers}
                      />
                    </div>

                    <MessageInput
                      onSendMessage={handleSendMessage}
                      disabled={false}
                      conversationId={selectedConversation}
                      currentUserId={user?.id || ""}
                    />
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                    <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <User className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-700 mb-2">No conversation selected</h3>
                    <p className="text-gray-500">
                      Choose a conversation from the list or start a new one.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={showAttachMedia} onOpenChange={setShowAttachMedia}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Attach Media</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                variant="default"
              >
                Select File
              </Button>
              <p className="text-sm text-gray-500">
                Supported formats: Images, Videos, Audio, PDF
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CallDialogs
        showVideoCall={showVideoCall}
        showVoiceCall={showVoiceCall}
        conversation={getSelectedConversation()}
        onCloseVideoCall={() => setShowVideoCall(false)}
        onCloseVoiceCall={() => setShowVoiceCall(false)}
        onEndCall={endCall}
      />

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            handleFileSelect(e.target.files[0]);
          }
        }}
        accept="image/*,video/*,audio/*,application/pdf"
      />

      <Footer />
    </div>
  );
};

export default Messages;
